import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaName,
  type CitationParts,
  invertedName,
  naturalName,
  type PersonName,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaUrl,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  apaContributors,
  type Contributors,
  chicagoBibliographyContributors,
  chicagoNoteContributors,
  mlaContributors,
  parsePeople,
  publisherName,
  type SpecialValues,
} from "@/lib/tool-kit/special-sources/common";

/*
 * Ancient Greek and Roman works — Homer, Plato, Aristotle, Virgil, Cicero — read in a
 * modern edition or translation, in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7: APA Style blog, "Citing classical and religious works" (D. Becker, April 5,
 *   2021) — a classical work is cited as a book (or webpage) in the edition used; the
 *   translator follows the title, "(H. F. Cary, Trans.)"; a known original date goes at
 *   the end, "(Original work published …)", with "B.C.E." after an ancient year and
 *   "ca." before an approximate one; an unknown or disputed original date is left
 *   out; in the text both years appear, "(Alighieri, 1909/2001, …)", "(The Epic of
 *   Gilgamesh, ca. 2750–2500 B.C.E./1998, Tablet II)", and quotations are located by
 *   "canonically numbered parts common across editions" instead of pages (Publication
 *   Manual p. 303; §8.13, "Citing specific parts of a source": "customize the wording
 *   to the work you are citing"). With no known author, the title takes the author's
 *   place ("The epic of Gilgamesh (M. G. Kovaks, Trans.). (1998). …").
 * - MLA 9: the works-cited entry is the edition used — MLA Style Center, "How do I cite
 *   nonconsecutive lines from a poem like Homer's The Odyssey?": "Homer. The Odyssey.
 *   Translated by Emily Wilson, W. W. Norton, 2018." In the text, commonly studied
 *   verse and prose are located by their divisions in arabic numerals separated by
 *   periods instead of page numbers (MLA Handbook 9th ed. §6.22), and a famous work
 *   still needs its author's name (MLA Style Center, "Do you have to provide the
 *   author's name … for a work of common literature such as The Iliad?"). Author and
 *   title are separated by a comma, the title and the numbers are not. The original
 *   date is an optional element; this generator leaves an ancient one out.
 * - Chicago 18: classical references go in notes, by the standard numbers that are the
 *   same in every edition and translation (§14.142–14.143), with no comma between the
 *   title and the numbers — "Aquinas, Summa Theologica 1.65.4" (CMOS Q&A on §14.143);
 *   "Plato, Republic 360e–361b". A modern edition or translation is cited like any book
 *   and listed in the bibliography when you quote it or use its editor's work (§14.142,
 *   §14.151; CMOS Q&A: "Aristotle. Nicomachean Ethics. Rev. ed. Edited by H.
 *   Rackham. …"). No place of publication for books published since 1900 (CMOS 18).
 * - Era abbreviations: APA writes "B.C.E." with periods (APA blog above); MLA writes
 *   capital-letter abbreviations without periods (BCE) and "ca." for circa (MLA
 *   Handbook 9th ed., appendix 1); Chicago writes BCE and CE without periods.
 */

export const CLASSICAL_WORK_FIELDS = {
  author: "author",
  title: "title",
  shortTitle: "shortTitle",
  translators: "translators",
  editors: "editors",
  publisher: "publisher",
  year: "year",
  url: "url",
  originalYear: "originalYear",
  era: "era",
  circa: "circa",
  location: "location",
} as const;

const F = CLASSICAL_WORK_FIELDS;

export type ClassicalEra = "bce" | "ce" | "none";

export interface ClassicalWorkLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  bibliography: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

const RE_LEADING_ARTICLE = /^(?:the|a|an)\s+/i;
const RE_ERA_TEXT = /\s*(?:B\.?C\.?E?\.?|C\.?E\.?|A\.?D\.?)\s*$/i;
const RE_BCE_TEXT = /B\.?C/i;
const RE_CIRCA_TEXT = /^\s*(?:ca?\.|circa)\s*/i;

/** The title readers know the work by: "The Odyssey" → "Odyssey". */
export function shortClassicalTitle(title: string): string {
  const main = title.split(":")[0].trim();
  return main.replace(RE_LEADING_ARTICLE, "") || main;
}

interface OriginalDate {
  year: string;
  era: ClassicalEra;
  circa: boolean;
}

/** The original date as typed, with any era or "ca." the user typed into the year. */
function originalDate(values: SpecialValues): OriginalDate | null {
  const raw = get(values, F.originalYear);
  const year = raw.replace(RE_CIRCA_TEXT, "").replace(RE_ERA_TEXT, "").trim();
  if (year === "") {
    return null;
  }
  // An era typed into the year ("375 BCE", "175 CE") wins over the era menu.
  const typedEra = RE_ERA_TEXT.exec(raw.trim());
  let era = get(values, F.era) as ClassicalEra;
  if (typedEra) {
    era = RE_BCE_TEXT.test(typedEra[0]) ? "bce" : "ce";
  } else if (!["bce", "ce", "none"].includes(era)) {
    era = "bce";
  }
  return {
    year,
    era,
    circa: get(values, F.circa) === "true" || RE_CIRCA_TEXT.test(raw),
  };
}

/** APA: "ca. 375 B.C.E."; Chicago and MLA would write "ca. 375 BCE". */
export function apaOriginalDate(date: OriginalDate): string {
  const era = { bce: " B.C.E.", ce: " C.E.", none: "" }[date.era];
  return `${date.circa ? "ca. " : ""}${date.year}${era}`;
}

interface ClassicalWork {
  author: PersonName | null;
  title: string;
  /** MLA and Chicago: "Odyssey". */
  short: string;
  /** APA, for a work with no author: "The epic of Gilgamesh". */
  apaShort: string;
  contributors: Contributors;
  publisher: string;
  year: string;
  url: string;
  original: OriginalDate | null;
  location: string;
}

/*
 * A classical author goes by one conventional name — Homer, Plato, Marcus Aurelius,
 * Pliny the Elder — which is never inverted or reduced to initials, so it is kept
 * whole, the way a group author is.
 */
function classicalAuthor(name: string): PersonName | null {
  return name === "" ? null : { given: [], family: name, organization: true };
}

function readWork(values: SpecialValues): ClassicalWork {
  const title = get(values, F.title);
  return {
    author: classicalAuthor(get(values, F.author)),
    title,
    short: get(values, F.shortTitle) || shortClassicalTitle(title),
    apaShort: get(values, F.shortTitle) || title.split(":")[0].trim(),
    contributors: {
      editors: parsePeople(get(values, F.editors)),
      translators: parsePeople(get(values, F.translators)),
    },
    publisher: get(values, F.publisher),
    year: get(values, F.year),
    url: get(values, F.url),
    original: originalDate(values),
    location: get(values, F.location),
  };
}

export function classicalWorkIsReady(values: SpecialValues): boolean {
  return get(values, F.title) !== "";
}

/* ------------------------------------------------------------------- APA */

function apaSource(work: ClassicalWork): string {
  const publisher = work.publisher ? publisherName(work.publisher, "apa") : "";
  const pieces: string[] = [];
  if (publisher) {
    pieces.push(`${publisher}${closer(publisher)}`);
  }
  if (work.url) {
    pieces.push(work.url);
  }
  if (work.original) {
    pieces.push(`(Original work published ${apaOriginalDate(work.original)})`);
  }
  return pieces.join(" ");
}

function apaOutput(
  work: ClassicalWork,
  labels: ClassicalWorkLabels
): CitationOutput {
  const year = work.year || "n.d.";
  const contributors = apaContributors(work.contributors);
  const title = capitalizeSubtitle(work.title);
  const after = contributors ? ` (${contributors}).` : ".";
  const dates = work.original
    ? `${apaOriginalDate(work.original)}/${year}`
    : year;
  const locator = work.location ? `, ${work.location}` : "";
  let reference: CitationParts;
  let parenthetical: CitationParts;
  let narrative: CitationParts;
  if (work.author) {
    const author = apaName(work.author);
    reference = [
      plain(`${author}${closer(author)} (${year}). `),
      italic(title),
      plain(`${after} ${apaSource(work)}`),
    ];
    parenthetical = [plain(`(${work.author.family}, ${dates}${locator})`)];
    narrative = [plain(`${naturalName(work.author)} (${dates})`)];
  } else {
    reference = [
      italic(title),
      plain(`${after} (${year}). ${apaSource(work)}`),
    ];
    // APA names an untitled work by its title, article and all: "The Epic of Gilgamesh".
    const inText = titleCase(work.apaShort);
    parenthetical = [
      plain("("),
      italic(inText),
      plain(`, ${dates}${locator})`),
    ];
    narrative = [italic(inText), plain(` (${dates})`)];
  }
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: tidyParts(reference) },
      { label: labels.parenthetical, parts: parenthetical },
      { label: labels.narrative, parts: narrative },
    ],
    note: labels.apaNote,
  };
}

/* ------------------------------------------------------------------- MLA */

function mlaOutput(
  work: ClassicalWork,
  labels: ClassicalWorkLabels
): CitationOutput {
  const title = titleCase(work.title);
  const contributors = mlaContributors(work.contributors);
  const publication = [
    contributors,
    work.publisher ? publisherName(work.publisher, "mla") : "",
    work.year,
    work.url ? mlaUrl(work.url) : "",
  ].filter(Boolean);
  const entry: CitationParts = [];
  if (work.author) {
    const author = invertedName(work.author);
    entry.push(plain(`${author}${closer(author)} `));
  }
  entry.push(italic(title));
  entry.push(plain(closer(title)));
  if (publication.length > 0) {
    entry.push(plain(` ${publication.join(", ")}.`));
  }
  const short = titleCase(work.short);
  const where = work.location ? ` ${work.location}` : "";
  const inText: CitationParts = work.author
    ? [plain(`(${work.author.family}, `), italic(short), plain(`${where})`)]
    : [plain("("), italic(short), plain(`${where})`)];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(entry) },
      { label: labels.inText, parts: inText },
    ],
    note: labels.mlaNote,
  };
}

/* --------------------------------------------------------------- Chicago */

/** "(W. W. Norton, 2018)" — the facts of publication in a full note. */
function chicagoFacts(work: ClassicalWork): string {
  const facts = [
    work.publisher ? publisherName(work.publisher, "chicago") : "",
    work.year,
  ]
    .filter(Boolean)
    .join(", ");
  return facts ? ` (${facts})` : "";
}

function chicagoOutput(
  work: ClassicalWork,
  labels: ClassicalWorkLabels
): CitationOutput {
  const title = titleCase(work.title);
  const short = titleCase(work.short);
  const name = work.author ? naturalName(work.author) : "";
  let note: CitationParts;
  if (work.location) {
    // The classical form: standard numbers, no page, no comma before them.
    note = [
      plain(name ? `${name}, ` : ""),
      italic(short),
      plain(` ${work.location}.`),
    ];
  } else {
    const contributors = chicagoNoteContributors(work.contributors);
    note = [
      plain(name ? `${name}, ` : ""),
      italic(title),
      plain(
        `${contributors ? `, ${contributors}` : ""}${chicagoFacts(work)}${
          work.url ? `, ${work.url}` : ""
        }.`
      ),
    ];
  }
  const bibliography: CitationParts = [];
  if (work.author) {
    const author = invertedName(work.author);
    bibliography.push(plain(`${author}${closer(author)} `));
  }
  bibliography.push(italic(title));
  bibliography.push(plain(closer(title)));
  const contributors = chicagoBibliographyContributors(work.contributors);
  const facts = [
    work.publisher ? publisherName(work.publisher, "chicago") : "",
    work.year,
  ]
    .filter(Boolean)
    .join(", ");
  const tail = [
    contributors,
    facts ? `${facts}.` : "",
    work.url ? `${work.url}.` : "",
  ].filter(Boolean);
  if (tail.length > 0) {
    bibliography.push(plain(` ${tail.join(" ")}`));
  }
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: tidyParts(note) },
      { label: labels.bibliography, parts: tidyParts(bibliography) },
    ],
    note: labels.chicagoNote,
  };
}

export function formatClassicalWork(
  values: SpecialValues,
  labels: ClassicalWorkLabels
): CitationOutput[] {
  const work = readWork(values);
  return [
    apaOutput(work, labels),
    mlaOutput(work, labels),
    chicagoOutput(work, labels),
  ];
}
